import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'pm:recent-projects';
const MAX_RECENT = 6;

export interface RecentProjectEntry {
  id: string;
  name: string;
  visitedAt: number;
}

const read = (): RecentProjectEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as RecentProjectEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const write = (entries: RecentProjectEntry[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    window.dispatchEvent(new Event('pm:recent-projects-changed'));
  } catch {
    // Ignore quota / privacy mode errors
  }
};

export const useRecentProjects = () => {
  const [recents, setRecents] = useState<RecentProjectEntry[]>(() => read());

  useEffect(() => {
    const handler = () => setRecents(read());
    window.addEventListener('pm:recent-projects-changed', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('pm:recent-projects-changed', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const trackVisit = useCallback((id: string, name: string) => {
    if (!id || !name) return;
    const current = read().filter((e) => e.id !== id);
    const next = [{ id, name, visitedAt: Date.now() }, ...current].slice(0, MAX_RECENT);
    write(next);
  }, []);

  const clear = useCallback(() => write([]), []);

  return { recents, trackVisit, clear };
};
